import React from "react";
import * as ReactDOM from "react-dom";
import { Icon, computeQuoteView, findCustomer, genToken } from "./registry";
import { QrCode, utmUrl } from "./lib/qr";
/* ============================================================
   Fence Works — Fence Plan (blueprint sheet)
   The quote becomes a drawing: dimensioned runs with letters, post
   marks, gate symbols + swing, the lot line & house as dashed
   references, a north arrow, an approximate scale bar, a run
   schedule, and a title block — generated straight from the same
   estimate the customer signed. Print pattern mirrors CrewBuildSheet
   (cs-overlay: window.print() prints only the sheet).
   ============================================================ */
const FP_money0 = window.VFP.money0;
const M_TO_FT = 3.28084;
const INK = "#1a2430", FAINT = "#8d99a8", GRID = "#e3ebf3";

const ftIn = (v: number) => { let f = Math.floor(v), i = Math.round((v - f) * 12); if (i === 12) { f += 1; i = 0; } return `${f}′-${i}″`; };
const runLetter = (n: number) => n < 26 ? String.fromCharCode(65 + n) : "R" + (n + 1);
/* stable identity for title blocks: the won job's number when one exists,
   else the auto-assigned quote number (FencePlanSheet mints Q-#### on first
   open), else the id-hash fallback for quotes from before numbering */
const qNoOf = (q: any, project: any) => (project && project.job) || (q.quoteNo ? "Q-" + q.quoteNo : "EST " + String(q.id).slice(-6).toUpperCase());

/* one drawn sheet for one fence snapshot */
function PlanSheet({ inputs, prod, calc, name, sheetNo, sheetCount, q, store, hubUrl }: any) {
  const parcel = inputs && inputs.parcel;
  const poly = parcel ? parcel.polygon : (inputs && inputs.mVerts) || [];
  if (!poly || poly.length < 2) return null;
  const closed = parcel ? parcel.closed !== false : false;
  const sides = parcel ? parcel.sides : poly.slice(1).map((b: any, i: number) => ({ i, a: poly[i], b, selected: true, lf: null, grade: "level" }));
  const gates = (parcel && parcel.gates) || [];
  const house = (parcel && parcel.house) || null;
  const lot = (parcel && parcel.rawPolygon) || null;

  // local-planar projection (meters), north-up — same math as the quote's SitePlan
  const k = Math.cos(poly[0][0] * Math.PI / 180);
  const prj = (pts: any[]) => pts.map(([la, ln]: any) => [ln * k * 111320, -la * 110540]);
  const pj = prj(poly), hj = house ? prj(house) : null;
  const boundPts = hj ? pj.concat(hj) : pj;                 // frame the fence + house; the lot line may run off-sheet
  const xs = boundPts.map(p => p[0]), ys = boundPts.map(p => p[1]);
  const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
  const w = (maxx - minx) || 1, h = (maxy - miny) || 1, pad = 0.16 * Math.max(w, h);
  const VW = 920, sc = VW / (w + 2 * pad);
  const VH = Math.max(360, Math.min(620, Math.round((h + 2 * pad) * sc)));
  const scY = VH / (h + 2 * pad), scale = Math.min(sc, scY);   // uniform scale, fits both axes
  const cx = (minx + maxx) / 2, cy = (miny + maxy) / 2;
  const TX = (x: number) => VW / 2 + (x - cx) * scale;
  const TY = (y: number) => VH / 2 + (y - cy) * scale;
  const P = (pt: any) => [TX(pt[0]), TY(pt[1])];
  const sp = pj.map(P);
  const pxPerFt = scale * 0.3048;
  const ftPerInch = Math.max(1, Math.round(96 / pxPerFt));  // CSS 96px/in → "1″ = X′-0″ (approx)"

  const selected = (sides || []).filter((s: any) => s.selected);
  const letterOf: any = {}; selected.forEach((s: any, idx: number) => { letterOf[s.i] = runLetter(idx); });
  const panelW = (calc && calc.panelWidth) || (prod && prod.panelWidth) || 8;
  const height = (prod && prod.height) || 6;
  const gradeNote = (s: any) => s.grade && s.grade !== "level" ? (String(s.grade).startsWith("step") ? " · stepped" : " · sloped") : "";
  const gateWord = (gw: number) => (gw >= 8 ? "DOUBLE GATE" : "SINGLE GATE") + " — " + ((prod && prod.category) || "FENCE").toUpperCase();

  // run label placement: midpoint pushed out along the wall's normal
  const spCx = sp.reduce((a, p) => a + p[0], 0) / (sp.length || 1);
  const spCy = sp.reduce((a, p) => a + p[1], 0) / (sp.length || 1);
  const labelSpot = (a: any, b: any, out = 22, flip = false) => {
    const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
    const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy) || 1;
    let nx = -dy / len, ny = dx / len;
    // push AWAY from the FENCE's own centroid so labels sit outside the fence
    // (the canvas centre drifts once the house frames into the sheet)
    if ((mx - spCx) * nx + (my - spCy) * ny < 0) { nx = -nx; ny = -ny; }
    if (flip) { nx = -nx; ny = -ny; }
    return [mx + nx * out, my + ny * out];
  };
  // a run carrying a gate labels on the INSIDE of the fence — the gate glyph
  // and its label own the outside midpoint, and both chips fighting for the
  // same spot buried the gate under "RUN E" on short runs
  const gateSides = new Set((gates || []).map((g: any) => g.side));

  const project = (store.projects || []).find((p: any) => p.id === q.projectId);
  const dateStr = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const mono: any = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };

  return (
    <div className="fence-plan" style={{ background: "#fff", color: INK, border: "1px solid " + INK, margin: "0 auto 18px", maxWidth: 980, pageBreakAfter: sheetNo < sheetCount ? "always" : "auto", ...mono }}>
      {/* header strip */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "9px 14px", borderBottom: "1px solid " + INK, fontSize: 11 }}>
        <span style={{ fontWeight: 800 }}><span style={{ color: "#0fb39a" }}>■</span> {(store.company && store.company.name) || (store.settings && store.settings.companyName) || "FenceWorks"} · Fence Plan{name ? " — " + name : ""}</span>
        <span style={{ color: FAINT }}>GENERATED FROM ESTIMATE</span>
        <span style={{ color: FAINT }}>REV R0</span>
        <span>SHEET A-0{sheetNo} / 0{sheetCount}</span>
      </div>

      {/* drawing */}
      <div style={{ position: "relative" }}>
        <svg viewBox={`0 0 ${VW} ${VH}`} width="100%" style={{ display: "block", background: "#fdfefe" }}>
          <defs>
            <pattern id={"fp-grid-" + sheetNo} width="14" height="14" patternUnits="userSpaceOnUse">
              <path d="M 14 0 L 0 0 0 14" fill="none" stroke={GRID} strokeWidth="1" />
            </pattern>
          </defs>
          <rect width={VW} height={VH} fill={`url(#fp-grid-${sheetNo})`} />
          {/* lot line + house: dashed references */}
          {lot && lot.length >= 3 && <polygon points={prj(lot).map(P).map(p => p.join(",")).join(" ")} fill="none" stroke={FAINT} strokeWidth="1" strokeDasharray="5 5" />}
          {hj && <polygon points={hj.map(P).map(p => p.join(",")).join(" ")} fill="none" stroke={FAINT} strokeWidth="1.2" strokeDasharray="4 3" />}
          {/* runs: unselected dashed light; selected solid ink with post squares */}
          {(sides || []).map((s: any) => {
            const a = P(pj[s.i]), b = P(pj[(s.i + 1) % pj.length]);
            if (!s.selected) return <line key={"u" + s.i} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={FAINT} strokeWidth="1" strokeDasharray="3 4" />;
            const lf = s.lf || 0;
            const n = Math.max(1, Math.round(lf / panelW));
            const posts = [] as any[];
            for (let i = 0; i <= n; i++) { const t = i / n; posts.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]); }
            return (
              <g key={"s" + s.i}>
                <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={INK} strokeWidth="2.2" />
                {posts.map((p, i) => <rect key={i} x={p[0] - 2.4} y={p[1] - 2.4} width="4.8" height="4.8" fill={INK} />)}
              </g>
            );
          })}
          {/* gates: white gap + span + quarter-circle swing */}
          {gates.map((g: any, gi: number) => {
            const s = (sides || []).find((x: any) => x.i === g.side); if (!s) return null;
            const a = P(pj[g.side]), b = P(pj[(g.side + 1) % pj.length]);
            const lf = s.lf || 1;
            const cxg = a[0] + (b[0] - a[0]) * g.t, cyg = a[1] + (b[1] - a[1]) * g.t;
            const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy) || 1;
            const half = Math.max(4, ((g.width || 4) / lf) * len / 2);
            const ux = dx / len, uy = dy / len, sgn = (g.swing & 2) ? 1 : -1;
            const hxg = cxg - ux * half, hyg = cyg - uy * half, r = half * 2;
            const [lx, ly] = labelSpot([cxg - ux * half, cyg - uy * half], [cxg + ux * half, cyg + uy * half], 30);
            return (
              <g key={"g" + gi}>
                <line x1={cxg - ux * half} y1={cyg - uy * half} x2={cxg + ux * half} y2={cyg + uy * half} stroke="#fdfefe" strokeWidth="5" />
                <line x1={cxg - ux * half} y1={cyg - uy * half} x2={cxg + ux * half} y2={cyg + uy * half} stroke={INK} strokeWidth="1.2" strokeDasharray="2 3" />
                <path d={`M ${hxg} ${hyg} L ${hxg + ux * r} ${hyg + uy * r} A ${r} ${r} 0 0 ${sgn > 0 ? 1 : 0} ${hxg + -uy * sgn * r} ${hyg + ux * sgn * r}`} fill="none" stroke={INK} strokeWidth="1" strokeDasharray="3 3" />
                <text x={lx} y={ly - 5} textAnchor="middle" fontSize="9.5" fontWeight="700" fill={INK} style={mono}>{gateWord(g.width || 4)}</text>
                <text x={lx} y={ly + 6} textAnchor="middle" fontSize="9" fill={FAINT} style={mono}>{ftIn(g.width || 4)}</text>
              </g>
            );
          })}
          {/* run letters + dimensions */}
          {selected.map((s: any) => {
            const a = P(pj[s.i]), b = P(pj[(s.i + 1) % pj.length]);
            const [lx, ly] = labelSpot(a, b, 24, gateSides.has(s.i));
            return (
              <g key={"lb" + s.i}>
                <rect x={lx - 27} y={ly - 10} width="54" height="13" fill="#fff" stroke={INK} strokeWidth="1" />
                <text x={lx} y={ly} textAnchor="middle" fontSize="9.5" fontWeight="800" fill={INK} style={mono}>RUN {letterOf[s.i]}</text>
                <text x={lx} y={ly + 13} textAnchor="middle" fontSize="9.5" fill={INK} style={mono}>{s.lf != null ? ftIn(s.lf) : ""}</text>
              </g>
            );
          })}
          {/* north arrow (projection is north-up) */}
          <g transform={`translate(${VW - 42}, 34)`}>
            <polygon points="0,-14 5,2 0,-2 -5,2" fill={INK} />
            <line x1="0" y1="2" x2="0" y2="22" stroke={INK} strokeWidth="1" />
            <text x="0" y="-19" textAnchor="middle" fontSize="11" fontWeight="800" fill={INK} style={mono}>N</text>
          </g>
          {/* scale bar */}
          <g transform={`translate(20, ${VH - 26})`}>
            <text x="0" y="-8" fontSize="9.5" fill={INK} style={mono}>SCALE · 1&#8243; = {ftPerInch}&#8242;-0&#8243; (APPROX)</text>
            {[0, 1, 2, 3].map(i => (
              <rect key={i} x={i * 10 * pxPerFt} y="0" width={10 * pxPerFt} height="6" fill={i % 2 ? "#fff" : INK} stroke={INK} strokeWidth="0.8" />
            ))}
            {[0, 10, 20, 30, 40].map(ft => (
              <text key={ft} x={ft * pxPerFt} y="16" textAnchor="middle" fontSize="8" fill={FAINT} style={mono}>{ft}&#8242;</text>
            ))}
          </g>
        </svg>
        {/* stats box over the drawing */}
        <div className="fp-stats" style={{ position: "absolute", left: 20, top: 20, background: "#fff", border: "1px solid " + INK, padding: "10px 14px", display: "flex", gap: 18, ...mono }}>
          <div>
            <div style={{ fontSize: 8.5, letterSpacing: ".08em", color: FAINT }}>TOTAL LINEAR FEET</div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>{Math.round((calc && calc.LF) || 0)} LF</div>
            <div style={{ fontSize: 8.5, color: FAINT }}>{selected.length} run{selected.length === 1 ? "" : "s"} · {gates.length} gate{gates.length === 1 ? "" : "s"} · {(calc && calc.posts) || 0} posts</div>
          </div>
          <div>
            <div style={{ fontSize: 8.5, letterSpacing: ".08em", color: FAINT }}>HEIGHT</div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>{height}&#8242;-0&#8243;</div>
            <div style={{ fontSize: 8.5, color: FAINT }}>All runs</div>
          </div>
        </div>
      </div>

      {/* run schedule + title block */}
      <div className="fp-titleblock" style={{ display: "flex", borderTop: "1px solid " + INK }}>
        <table style={{ flex: 1.5, borderCollapse: "collapse", fontSize: 10.5, ...mono }}>
          <thead>
            <tr>{["RUN", "LENGTH", "STYLE", "NOTES"].map(hd => <th key={hd} style={{ textAlign: "left", padding: "6px 10px", borderBottom: "1px solid " + INK, borderRight: "1px solid " + GRID, fontSize: 8.5, letterSpacing: ".08em", color: FAINT }}>{hd}</th>)}</tr>
          </thead>
          <tbody>
            {selected.map((s: any) => (
              <tr key={s.i}>
                <td style={{ padding: "5px 10px", borderBottom: "1px solid " + GRID, fontWeight: 800 }}>{letterOf[s.i]}</td>
                <td style={{ padding: "5px 10px", borderBottom: "1px solid " + GRID }}>{s.lf != null ? ftIn(s.lf) : "—"}</td>
                <td style={{ padding: "5px 10px", borderBottom: "1px solid " + GRID }}>{(prod && prod.name) || "Fence"}</td>
                <td style={{ padding: "5px 10px", borderBottom: "1px solid " + GRID }}>{height}&#8242; HT · {panelW}&#8242; O.C.{gradeNote(s)}</td>
              </tr>
            ))}
            {gates.map((g: any, gi: number) => (
              <tr key={"g" + gi}>
                <td style={{ padding: "5px 10px", borderBottom: "1px solid " + GRID, fontWeight: 800 }}>G{gi + 1}</td>
                <td style={{ padding: "5px 10px", borderBottom: "1px solid " + GRID }}>{ftIn(g.width || 4)}</td>
                <td style={{ padding: "5px 10px", borderBottom: "1px solid " + GRID }}>{(prod && prod.category) || ""} gate</td>
                <td style={{ padding: "5px 10px", borderBottom: "1px solid " + GRID }}>{letterOf[g.side] ? "on Run " + letterOf[g.side] : "—"}{(g.width || 4) >= 8 ? " · double drive" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="fp-title-meta" style={{ flex: 1, borderLeft: "1px solid " + INK, padding: "10px 14px", display: "flex", gap: 12, justifyContent: "space-between", alignItems: "flex-start", ...mono }}>
          <div>
            <div style={{ fontSize: 8.5, letterSpacing: ".08em", color: FAINT }}>PROJECT</div>
            <div style={{ fontSize: 15, fontWeight: 800, margin: "2px 0 6px", textTransform: "uppercase" }}>{q.customer || "Fence Plan"}</div>
            <div style={{ fontSize: 10.5, lineHeight: 1.6 }}>
              {q.address && <div>{q.address}</div>}
              <div style={{ fontWeight: 800 }}>FENCE PLAN · {qNoOf(q, project)}</div>
              <div style={{ color: FAINT }}>{["Quote " + FP_money0(q.total || 0), dateStr].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
          {hubUrl && <QrCode url={utmUrl(hubUrl, "blueprint")} size={62} label="PROJECT PORTAL" />}
        </div>
      </div>
      <div style={{ padding: "6px 14px", borderTop: "1px solid " + INK, fontSize: 7.5, letterSpacing: ".05em", color: FAINT, ...mono }}>
        DRAWING IS ILLUSTRATIVE · FINAL LAYOUT SUBJECT TO SITE CONDITIONS, UTILITY LOCATES, SETBACKS, AND HOA APPROVAL · NOT A SUBSTITUTE FOR A SURVEYED SITE PLAN
      </div>
    </div>
  );
}

/* Sheet 2 — install specifications, pre-job checklist & payment terms.
   The customer-facing packet page: what we're installing (post depth, frost
   line, spacing), what we need from them before install day, and how payment
   works — every line generated from the same estimate + workspace settings. */
function SpecsSheet({ q, store, product, calc, settings, sheetNo, sheetCount, hubUrl, hubToken }: any) {
  const mono: any = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };
  const s = settings || {};
  const cat = (product && product.category) || "Fence";
  const height = (product && product.height) || (product && product.heightFt) || 6;
  const panelW = (product && product.panelWidth) || 8;
  const posts = Math.round((calc && calc.posts) || 0), panels = Math.round((calc && calc.panels) || 0), LF = Math.round((calc && calc.LF) || q.lf || 0);
  const gates = Math.round((calc && calc.gates) || 0);
  // ---- post spec comes FROM THE PRODUCT'S cost sheet, not a hardcoded guess:
  // the Post-type component rows name the exact post ("Galvanized Steel Post -
  // 2-3/8″ Schedule 40 - 8′") — quote a different product, the sheet follows.
  // Install METHOD is data-driven too: a Concrete row on the product = poured
  // footings (+ cure-time note); no concrete row = DRIVEN posts, no concrete.
  const comps = (product && product._editor && Array.isArray(product._editor.components)) ? product._editor.components : [];
  const postComps = comps.filter((c: any) => c && c.type === "Post" && !/kit|cap|fitting|collar|sleeve kit/i.test(String(c.desc || "")));
  const postDesc = postComps.map((c: any) => c.desc).filter(Boolean).join(" · ");
  const hasConcrete = comps.length ? comps.some((c: any) => c && c.type === "Concrete") : ((product && product.bagsPerPost) || 0) > 0;
  const postFallback = /vinyl/i.test(cat)
    ? "galvanized steel posts inside heavy-wall vinyl sleeves — steel carries the load, vinyl never rots or peels"
    : /wood/i.test(cat) ? "4×4 pressure-treated posts"
    : /chain/i.test(cat) ? "galvanized pipe posts"
    : "heavy-duty posts per manufacturer spec";
  const postMethod = hasConcrete
    ? "set 42–48″ deep — below local frost line — in concrete footings, plumbed & strung to line"
    : "DRIVEN 42–48″ deep — below local frost line — no concrete: compacted native soil grips the post full-depth, the install stays clean, and the fence hangs the same day";
  const SPEC: any[] = [
    ["Fence", `${(product && product.name) || cat} · ${height}′ height · ${LF} linear ft · ${panels} panels @ ${panelW}′ O.C.`],
    [`Posts (${posts})`, `${postDesc || postFallback} · ${postMethod}`],
    gates > 0 ? [`Gate${gates > 1 ? "s" : ""} (${gates})`, "matching leaf on dedicated heavier gate posts · adjustable hinges + lockable latch · set to swing clear and self-catch"] : null,
    ["Hardware", "manufacturer-spec fasteners, brackets & caps throughout · coated/stainless screws — no rust streaks"],
    ["Cleanup", "post spoils raked out or hauled per quote · magnet sweep for fasteners · final walk-through with you before we leave"],
  ].filter(Boolean);
  const dep = s.deposit || {};
  const depTxt = dep.mode === "full_only" ? "full balance due at signing"
    : dep.mode === "custom" ? `${Math.max(1, Math.min(99, Number(dep.customPct) || 50))}% deposit due at signing · balance on completion`
    : `deposit due at signing (${(Array.isArray(dep.options) && dep.options.length ? dep.options : [25, 50]).join("% / ")}% options) · balance on completion`;
  const cf = s.cardFee || {};
  const PAY = [
    depTxt,
    cf.enabled ? `cash, bank transfer (ACH) & check — no fee · credit card adds ${Math.max(1, Math.min(3, Number(cf.pct) || 3))}% processing` : "pay online (card / bank) from your customer hub, or by cash / check",
    (Number(s.taxRate) || 0) > 0 ? `sales tax (${String(+((Number(s.taxRate) || 0) * 100).toFixed(3))}%) itemized on the invoice` : null,
    "pricing valid 30 days · changes after signing ride a written, e-signed change order",
  ].filter(Boolean);
  const warrantyYears = s.warrantyYears != null && isFinite(+s.warrantyYears) && +s.warrantyYears > 0 ? +s.warrantyYears : 1;
  const PREP = [
    "WE call 811 for public utility locates before digging (allow 2–3 business days)",
    "YOU mark private lines locates can't see: irrigation, landscape lighting, pet fence, lines to sheds/pools",
    "property line: we'll help locate pins, but the FINAL fence position needs your sign-off — share any survey/blueprint you have; we build to the line you approve and aren't liable if that line is off",
    "clear a 3′ working path along the fence line — or have us quote the clearing (confirmed at the site visit; it rides your estimate as its own line item)",
    "unlock side gates · keep pets inside on install days · sprinklers OFF the day posts are set",
    hasConcrete ? "concrete cures overnight — please keep hands (and dogs) off new posts for 24 hours" : null,
  ].filter(Boolean);
  const dateStr = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return (
    <div className="fence-plan" style={{ background: "#fff", color: INK, border: "1px solid " + INK, margin: "0 auto 18px", maxWidth: 980, ...mono }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "9px 14px", borderBottom: "1px solid " + INK, fontSize: 11 }}>
        <span style={{ fontWeight: 800 }}><span style={{ color: "#0fb39a" }}>■</span> {(store.company && store.company.name) || s.companyName || "FenceWorks"} · Install Specs &amp; Pre-Job</span>
        <span style={{ color: FAINT }}>GENERATED FROM ESTIMATE</span>
        <span style={{ color: FAINT }}>REV R0</span>
        <span>SHEET A-0{sheetNo} / 0{sheetCount}</span>
      </div>
      <div style={{ padding: "16px 18px", display: "grid", gap: 16 }}>
        <div>
          <div style={{ fontSize: 8.5, letterSpacing: ".08em", color: FAINT }}>PROJECT</div>
          <div style={{ fontSize: 15, fontWeight: 800, textTransform: "uppercase" }}>{q.customer || "Fence project"} — what to expect</div>
          <div style={{ fontSize: 10.5, color: FAINT }}>{[q.address, qNoOf(q, null), dateStr].filter(Boolean).join(" · ")}</div>
        </div>
        <div>
          <div style={{ fontSize: 9, letterSpacing: ".1em", color: FAINT, borderBottom: "1px solid " + INK, paddingBottom: 4, marginBottom: 8 }}>INSTALL SPECIFICATIONS</div>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 10.5 }}><tbody>
            {SPEC.map(([k, v]: any) => (
              <tr key={k}>
                <td style={{ padding: "5px 12px 5px 0", fontWeight: 800, whiteSpace: "nowrap", verticalAlign: "top", borderBottom: "1px solid " + GRID }}>{k}</td>
                <td style={{ padding: "5px 0", borderBottom: "1px solid " + GRID, lineHeight: 1.55 }}>{v}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div>
            <div style={{ fontSize: 9, letterSpacing: ".1em", color: FAINT, borderBottom: "1px solid " + INK, paddingBottom: 4, marginBottom: 8 }}>PRE-JOB CHECKLIST — BEFORE INSTALL DAY</div>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 10.5, lineHeight: 1.8 }}>
              {PREP.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </div>
          <div>
            <div style={{ fontSize: 9, letterSpacing: ".1em", color: FAINT, borderBottom: "1px solid " + INK, paddingBottom: 4, marginBottom: 8 }}>PAYMENT &amp; TERMS</div>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 10.5, lineHeight: 1.8 }}>
              {PAY.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
            <div style={{ fontSize: 9, letterSpacing: ".1em", color: FAINT, borderBottom: "1px solid " + INK, padding: "12px 0 4px", marginBottom: 8 }}>WARRANTY</div>
            <p style={{ margin: 0, fontSize: 10.5, lineHeight: 1.6 }}>Workmanship warranted {warrantyYears} year{warrantyYears === 1 ? "" : "s"} · materials carry the manufacturer's warranty · full Terms &amp; Conditions (fw-terms-v1) are on your e-sign page.</p>
          </div>
        </div>
        {/* the paper → digital return path: every code goes to a DIFFERENT
            destination and every URL is utm-tagged (source=fenceworks,
            medium=qr) so scans are attributable per doc + per placement */}
        {(hubUrl || (settings && settings.googleReviewUrl)) && (
          <div style={{ display: "flex", gap: 26, justifyContent: "flex-end", alignItems: "flex-start", borderTop: "1px solid " + GRID, paddingTop: 12 }}>
            {hubUrl && <QrCode url={utmUrl(hubUrl, "specs-portal")} size={68} label="PROJECT PORTAL" sub="quote · e-sign · pay · schedule" />}
            {hubToken && <QrCode url={utmUrl(window.location.origin + "/build3d/" + hubToken, "specs-3d")} size={68} label="YOUR FENCE IN 3D" sub="watch it built step by step" />}
            {settings && settings.googleReviewUrl && <QrCode url={utmUrl(settings.googleReviewUrl, "specs-review", "review")} size={68} label="RATE YOUR EXPERIENCE" sub="30 seconds · means the world" />}
          </div>
        )}
      </div>
      <div style={{ padding: "6px 14px", borderTop: "1px solid " + INK, fontSize: 7.5, letterSpacing: ".05em", color: FAINT, ...mono }}>
        PART OF YOUR JOB PACKET{(store.company && store.company.phone) ? " · QUESTIONS? CALL " + store.company.phone : ""} · SCHEDULE MAY SHIFT FOR WEATHER, SITE ACCESS, OR MATERIAL AVAILABILITY
      </div>
    </div>
  );
}

/* FencePlanSheet — the overlay: preview on screen, window.print() prints only
   the sheet(s). Multi-fence estimates get one lettered sheet per fence. */
function FencePlanSheet({ quote: q, store, settings, onClose }: any) {
  const sIn = settings || (store && store.settings) || {};
  const view = computeQuoteView(q, sIn);
  const { product, fenceQuotes, quote } = view;
  React.useEffect(() => {
    const f = (e: any) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, [onClose]);
  // auto-assign a permanent quote number the first time a packet is generated
  // — a blueprint title block wants "Q-1043", not a random id hash. Sequence
  // rides settings.quoteSeq; existing quotes keep whatever number they got.
  React.useEffect(() => {
    if (!store || !store.updateQuote || q.quoteNo) return;
    const seq = Math.max(1000, Number((store.settings || {}).quoteSeq) || 1000) + 1;
    store.updateQuote(q.id, { quoteNo: seq });
    store.setSettings && store.setSettings({ quoteSeq: seq });
  }, [q.id]);
  // the packet's QR codes need the customer's hub token — mint one if the
  // linked customer doesn't have theirs yet (same token the share link uses)
  const cust = (store && ((store.customers || []).find((c: any) => c.id === q.customerId) || findCustomer(store.customers || [], q.customer, q.email))) || null;
  React.useEffect(() => {
    if (!store || !store.updateCustomer || !cust || cust.hubToken) return;
    store.updateCustomer(cust.id, { hubToken: genToken() });
  }, [cust && cust.id]);
  const hubToken = cust && cust.hubToken;
  const hubUrl = hubToken ? window.location.origin + "/hub/" + hubToken : null;
  const sections = fenceQuotes
    ? fenceQuotes.map((f: any) => ({ name: f.name, prod: f.prod, calc: f.q, inputs: f.snap }))
    : [{ name: null, prod: product, calc: quote, inputs: view.inputs }];
  const drawable = sections.filter((s: any) => { const poly = (s.inputs && s.inputs.parcel && s.inputs.parcel.polygon) || (s.inputs && s.inputs.mVerts) || []; return poly.length >= 2; });
  // packet composition (Settings → Job Packet): which generated sheets print,
  // plus the contractor's own uploaded pages appended in their order
  const pk = (sIn.jobPacket || {});
  const inc = Object.assign({ blueprint: true, specs: true }, pk.include || {});
  const uploads = (Array.isArray(pk.docs) ? pk.docs : []).filter((d: any) => d && d.on !== false && Array.isArray(d.pages) && d.pages.length);
  const planSheets = inc.blueprint ? drawable : [];
  const genCount = planSheets.length + (inc.specs ? 1 : 0);
  return ReactDOM.createPortal(
    <div className="cs-overlay" data-dialog onClick={onClose}>
      <div className="cs-modal" onClick={(e: any) => e.stopPropagation()}>
        <div className="cs-bar no-print">
          <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="map" size={14} className="muted" />Job packet — blueprint &amp; install specs</span>
          <div className="row gap-8">
            <button className="btn btn-primary btn-sm" onClick={() => window.print()}><Icon name="printer" size={14} />Print / PDF</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
          </div>
        </div>
        <div className="cs-scroll" style={{ background: "#3a4353" }}>
          {inc.blueprint && drawable.length === 0 && (
            <div style={{ background: "#fff", color: INK, padding: 40, textAlign: "center", margin: "0 auto 18px", maxWidth: 640 }}>This estimate has no drawn fence (manual footage only) — open it in the Estimator and outline the runs to generate the plan sheet. The specs sheet below still applies.</div>
          )}
          {planSheets.map((s: any, i: number) => (
            <PlanSheet key={i} inputs={s.inputs} prod={s.prod} calc={s.calc} name={s.name} sheetNo={i + 1} sheetCount={genCount} q={q} store={store} hubUrl={hubUrl} />
          ))}
          {inc.specs && <SpecsSheet q={q} store={store} product={product} calc={quote} settings={sIn} sheetNo={planSheets.length + 1} sheetCount={genCount} hubUrl={hubUrl} hubToken={hubToken} />}
          {/* the contractor's own packet pages (Settings → Job Packet uploads):
              sell sheets, warranty card, referral offer — full-page, in order */}
          {uploads.map((d: any) => (d.pages || []).map((p: string, pi: number) => (
            <div key={d.id + "-" + pi} className="fence-plan" style={{ background: "#fff", border: "1px solid " + INK, margin: "0 auto 18px", maxWidth: 980, pageBreakAfter: "always" }}>
              <img src={p} alt={(d.title || "Packet page") + " " + (pi + 1)} style={{ width: "100%", display: "block" }} />
            </div>
          )))}
          {genCount === 0 && uploads.length === 0 && (
            <div style={{ background: "#fff", color: INK, padding: 40, textAlign: "center", margin: "0 auto", maxWidth: 640 }}>Every packet sheet is switched off — turn sheets on (or upload your documents) under Settings → Job Packet.</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

Object.assign(window, { FencePlanSheet });

export { FencePlanSheet, PlanSheet };
